import { Popover } from "@radix-ui/themes";
import { HelpCircle, LogOut, Menu, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isGuestOrNoWallet } from "@shared/domain/guestSession";
import { useAccountId } from "../../auth/useAccountId";
import { useUnifiedLogout } from "../../auth/useUnifiedLogout";
import { fetchTrainerProfile } from "../../api/petBackendApi";
import { TrainerAvatar } from "../TrainerAvatar";
import { AudioMuteButton } from "./AudioMuteButton";
import { useT } from "../../i18n/i18nContext";

/** Avatar diameter for the popover header (matches the rail-foot avatar). */
const TRAINER_MENU_AVATAR_SIZE = 40;
const TRAINER_MENU_ICON_SIZE = 18;

/**
 * Rail-foot trainer menu (desktop). A single ☰ button beside the profile avatar that opens a
 * popover: who is signed in, then Profile, Help, the sound toggle and Log out.
 *
 * Help and Sound live here instead of as their own rail entries so the rail fits a normal
 * screen without a scrollbar. Web2: no wallet, no funds — the old wallet card
 * (TrainerWalletCard) is parked. Guests get the menu too (Help and Sound are for everyone);
 * only Log out is for a signed-in trainer.
 */
export function GameTrainerMenu() {
    const navigate = useNavigate();
    const t = useT();
    const handleLogout = useUnifiedLogout();
    const { address } = useAccountId();
    const isGuest = isGuestOrNoWallet(address);

    // Shares the query key + cache with GameProfileCard's rail avatar, so opening the menu
    // reuses the already-fetched profile rather than refetching.
    const { data: profile } = useQuery({
        queryKey: ["trainerProfile", address],
        queryFn: () => fetchTrainerProfile(address as string),
        enabled: !!address && !isGuest,
        staleTime: 60_000,
    });

    const displayName = profile?.displayName?.trim();

    return (
        <Popover.Root>
            <Popover.Trigger>
                <button type="button" className="game-nav__wallet-btn" aria-label={t("navigation.game.trainerMenu")}>
                    <Menu size={20} strokeWidth={2} aria-hidden="true" />
                </button>
            </Popover.Trigger>
            <Popover.Content side="right" align="end" size="1" className="game-popover game-wallet-popover">
                <div className="game-wallet-popover__identity">
                    <span className="game-wallet-popover__avatar">
                        <TrainerAvatar avatar={profile?.avatar ?? null} size={TRAINER_MENU_AVATAR_SIZE} />
                    </span>
                    <span className="game-wallet-popover__name" title={displayName || undefined}>
                        {isGuest ? "Guest" : displayName || "Trainer"}
                    </span>
                </div>
                <div className="game-trainer-menu">
                    <Popover.Close>
                        <button type="button" className="game-trainer-menu__row" onClick={() => navigate("/profile")}>
                            <User size={TRAINER_MENU_ICON_SIZE} strokeWidth={2} aria-hidden="true" />
                            <span>{t("navigation.game.profile")}</span>
                        </button>
                    </Popover.Close>
                    <Popover.Close>
                        <button type="button" className="game-trainer-menu__row" onClick={() => navigate("/help")}>
                            <HelpCircle size={TRAINER_MENU_ICON_SIZE} strokeWidth={2} aria-hidden="true" />
                            <span>{t("navigation.game.help")}</span>
                        </button>
                    </Popover.Close>
                    <AudioMuteButton variant="menu" />
                    {!isGuest && (
                        <Popover.Close>
                            <button type="button" className="game-trainer-menu__row game-trainer-menu__row--logout" onClick={handleLogout}>
                                <LogOut size={TRAINER_MENU_ICON_SIZE} strokeWidth={2} aria-hidden="true" />
                                <span>{t("navigation.game.logout")}</span>
                            </button>
                        </Popover.Close>
                    )}
                </div>
            </Popover.Content>
        </Popover.Root>
    );
}
